import {
  clarityEvent,
  claritySet,
  getClarityPageGroup,
  getClarityPageTemplate,
  getDeviceGroup,
  isClaritySensitivePath,
} from "./clarity";
import { getLocaleFromPathname, stripLocalePrefix } from "./localeRouting";
import { trackMetaPixel } from "./fbPixel";
import { isTrackingHostAllowed } from "./tracking";

const ANALYTICS_CONTEXT_KEY = "__lukkeyAnalyticsContext";

const getGoogleTag = () => {
  if (typeof window === "undefined") return null;
  return typeof window.gtag === "function" ? window.gtag : null;
};

const sanitizeAnalyticsValue = (value) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  const normalized = String(value).trim();
  return normalized ? normalized : undefined;
};

const sanitizeAnalyticsParams = (params = {}) =>
  Object.entries(params).reduce((acc, [key, value]) => {
    const normalizedValue = sanitizeAnalyticsValue(value);
    if (!key || normalizedValue === undefined) return acc;
    acc[key] = normalizedValue;
    return acc;
  }, {});

const getStoredAnalyticsContext = () => {
  if (typeof window === "undefined") return {};
  return window[ANALYTICS_CONTEXT_KEY] || {};
};

const setStoredAnalyticsContext = (context) => {
  if (typeof window === "undefined") return {};
  window[ANALYTICS_CONTEXT_KEY] = context;
  return context;
};

export const setAnalyticsContext = (params = {}) => {
  const nextContext = sanitizeAnalyticsParams(params);
  const mergedContext = {
    ...getStoredAnalyticsContext(),
    ...nextContext,
  };

  setStoredAnalyticsContext(mergedContext);
  Object.entries(nextContext).forEach(([key, value]) => {
    claritySet(key, value);
  });

  return mergedContext;
};

export const getAnalyticsContext = () => getStoredAnalyticsContext();

export const getCartValueBucket = (total) => {
  const amount = Number(total || 0);
  if (amount <= 0) return "0";
  if (amount <= 1) return "reserve_deposit";
  if (amount < 100) return "lt_100";
  if (amount < 300) return "100_299";
  if (amount < 600) return "300_599";
  return "600_plus";
};

export const buildPageAnalyticsContext = (pathname = "/", overrides = {}) => {
  const normalizedPath = stripLocalePrefix(String(pathname || "/"));
  return sanitizeAnalyticsParams({
    page_path_base: normalizedPath,
    page_group: getClarityPageGroup(normalizedPath) || "other",
    page_template: getClarityPageTemplate(normalizedPath) || normalizedPath,
    locale: getLocaleFromPathname(pathname) || "en",
    device_group: getDeviceGroup(),
    ...overrides,
  });
};

export const trackAnalyticsEvent = (
  eventName,
  {
    params = {},
    clarityTags = {},
    metaPixelEvent,
    metaPixelParams,
  } = {},
) => {
  if (!eventName || typeof window === "undefined") return;
  if (!isTrackingHostAllowed()) return;
  if (isClaritySensitivePath(window.location.pathname)) return;

  const mergedParams = sanitizeAnalyticsParams({
    ...getStoredAnalyticsContext(),
    ...params,
  });
  const mergedClarityTags = sanitizeAnalyticsParams({
    ...clarityTags,
  });

  Object.entries(mergedClarityTags).forEach(([key, value]) => {
    claritySet(key, value);
  });
  clarityEvent(eventName);

  const gtag = getGoogleTag();
  if (gtag) {
    gtag("event", String(eventName), mergedParams);
  }

  if (metaPixelEvent) {
    trackMetaPixel(metaPixelEvent, metaPixelParams);
  }
};

export const trackAnalyticsPageView = (params = {}) => {
  if (typeof window === "undefined") return;
  if (!isTrackingHostAllowed()) return;
  if (isClaritySensitivePath(window.location.pathname)) return;

  const context = setAnalyticsContext(params);
  const gtag = getGoogleTag();
  if (!gtag) return;

  gtag("event", "page_view", {
    ...context,
    page_location: window.location.href,
    page_path: `${window.location.pathname}${window.location.search}`,
    page_title:
      typeof document !== "undefined" ? document.title || undefined : undefined,
  });
};
